import baseAPI from "@/api/base";
import { AxiosResponse } from "axios";
import {
  AGENT_ACTIONS_URL,
  AGENT_CHAT_URL,
  AGENT_CONTACTS_IMPORT_URL,
  AGENT_CONTACTS_LOOKUP_URL,
  AGENT_CONTACTS_SAVE_URL,
  AGENT_CONVERSATIONS_URL,
  AGENT_LINKEDIN_STATUS_URL,
  AGENT_UNREAD_REPLY_DRAFTS_URL,
} from "@/utils/urls";
import type {
  AgentActionDecision,
  AgentActionStatus,
  AgentChatRequest,
  AgentContactActionInput,
  AgentContactImportRequest,
} from "@/dtos/Agent.dto";

// One turn can run several model + LinkedIn calls, well past the default 30s.
const AGENT_TIMEOUT_MS = 5 * 60 * 1000;

class AgentService {
  static chat = async (data: AgentChatRequest): Promise<AxiosResponse> => {
    if (!data.file) return baseAPI.post(AGENT_CHAT_URL, data, { timeout: AGENT_TIMEOUT_MS });

    // An attached spreadsheet goes up as multipart, with the message beside it.
    const formData = new FormData();
    formData.append("file", data.file);
    if (data.message) formData.append("message", data.message);
    if (data.conversationId) formData.append("conversationId", data.conversationId);
    if (data.turnId) formData.append("turnId", data.turnId);
    if (data.model) formData.append("model", data.model);
    if (data.mode) formData.append("mode", data.mode);
    return baseAPI.post(AGENT_CHAT_URL, formData, {
      headers: { "Content-Type": "multipart/form-data" },
      timeout: AGENT_TIMEOUT_MS,
    });
  };

  static getLinkedInStatus = async (): Promise<AxiosResponse> =>
    baseAPI.get(AGENT_LINKEDIN_STATUS_URL);

  static listConversations = async (page = 1, limit = 50): Promise<AxiosResponse> =>
    baseAPI.get(AGENT_CONVERSATIONS_URL, { params: { page, limit } });

  static getConversation = async (id: string): Promise<AxiosResponse> =>
    baseAPI.get(`${AGENT_CONVERSATIONS_URL}/${id}`);

  static renameConversation = async (id: string, title: string): Promise<AxiosResponse> =>
    baseAPI.patch(`${AGENT_CONVERSATIONS_URL}/${id}`, { title });

  static deleteConversation = async (id: string): Promise<AxiosResponse> =>
    baseAPI.delete(`${AGENT_CONVERSATIONS_URL}/${id}`);

  /** The rep opened a thread; clears an AI reply draft thread's unread mark. */
  static markConversationRead = async (id: string): Promise<AxiosResponse> =>
    baseAPI.post(`${AGENT_CONVERSATIONS_URL}/${id}/read`);

  /** AI reply drafts the rep hasn't opened yet, newest first. */
  static listUnreadReplyDrafts = async (): Promise<AxiosResponse> =>
    baseAPI.get(AGENT_UNREAD_REPLY_DRAFTS_URL);

  /** Enrich the picked people in the background and drop them in a bucket. */
  static importContacts = async (data: AgentContactImportRequest): Promise<AxiosResponse> =>
    baseAPI.post(AGENT_CONTACTS_IMPORT_URL, data);

  /** Make sure the picked people exist as contacts; answers with their contact ids. */
  static saveContacts = async (contacts: AgentContactActionInput[]): Promise<AxiosResponse> =>
    baseAPI.post(AGENT_CONTACTS_SAVE_URL, { contacts }, { timeout: AGENT_TIMEOUT_MS });

  /** What the CRM holds for the picked people (email, phone, lookup still running). */
  static lookupContacts = async (contacts: AgentContactActionInput[]): Promise<AxiosResponse> =>
    baseAPI.post(AGENT_CONTACTS_LOOKUP_URL, { contacts });

  /** Drafted actions; `source: "reply"` keeps only AI reply drafts for a contact's messages. */
  static listActions = async (params: {
    status?: AgentActionStatus | "open";
    contactId?: string;
    source?: "reply";
    limit?: number;
  }): Promise<AxiosResponse> => baseAPI.get(AGENT_ACTIONS_URL, { params });

  /** One action the CRM assistant drafted, with its current status. */
  static getAction = async (id: string): Promise<AxiosResponse> =>
    baseAPI.get(`${AGENT_ACTIONS_URL}/${id}`);

  /**
   * Approve and send at once; answers with the action as it ended (executed or failed).
   * `payload` is the rep's edit, the whole input in the action's own shape.
   */
  static approveAction = async (
    id: string,
    payload?: Record<string, unknown>
  ): Promise<AxiosResponse> =>
    baseAPI.post(`${AGENT_ACTIONS_URL}/${id}/approve`, payload ? { payload } : {}, {
      timeout: AGENT_TIMEOUT_MS,
    });

  /** `reason` goes back to the agent. */
  static rejectAction = async (id: string, reason?: string): Promise<AxiosResponse> =>
    baseAPI.post(`${AGENT_ACTIONS_URL}/${id}/reject`, reason ? { reason } : {});

  /** Approved actions are sent in the background; poll getAction for how each ends. */
  static decideActions = async (
    ids: string[],
    decision: AgentActionDecision,
    reason?: string
  ): Promise<AxiosResponse> =>
    baseAPI.post(`${AGENT_ACTIONS_URL}/bulk`, { ids, decision, ...(reason ? { reason } : {}) });
}

export default AgentService;
